import { useState } from "react";
import {
  AspectRatio,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Center,
  Container,
  HStack,
  SafeAreaView,
  ScrollArea,
  Separator,
  SimpleGrid,
  Split,
  Stack,
  VStack,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

export function LayoutingSection() {
  const [viewportMode, setViewportMode] = useState<"desktop" | "tablet" | "mobile">("desktop");

  const viewportWidthClass =
    viewportMode === "mobile"
      ? "max-w-[375px]"
      : viewportMode === "tablet"
        ? "max-w-[768px]"
        : "w-full";

  return (
    <Section
      id="layouting-sota"
      eyebrow="Layout"
      title="Primitives de disposition"
      description="Container, Stack, SimpleGrid, Split, AspectRatio, SafeAreaView, ScrollArea. Grille fluide, sans layout shift, du téléphone à l'écran large."
    >
      {/* --------------------------------------------------- Simulateur de viewport */}
      <div className="rounded-xl border border-border bg-surface-raised p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-success" />
            <span className="text-xs font-medium text-fg">Simulateur de viewport</span>
          </div>
          <HStack gap={2}>
            <Button
              size="xs"
              variant={viewportMode === "desktop" ? "primary" : "outline"}
              onClick={() => setViewportMode("desktop")}
            >
              Bureau
            </Button>
            <Button
              size="xs"
              variant={viewportMode === "tablet" ? "primary" : "outline"}
              onClick={() => setViewportMode("tablet")}
            >
              768 px
            </Button>
            <Button
              size="xs"
              variant={viewportMode === "mobile" ? "primary" : "outline"}
              onClick={() => setViewportMode("mobile")}
            >
              375 px
            </Button>
          </HStack>
        </div>

        <div className="mt-4 flex justify-center overflow-hidden">
          <div
            className={`transition-[max-width,width] duration-slow ease-standard contain-layout ${viewportWidthClass} rounded-lg border border-dashed border-border-strong bg-surface-sunken p-4`}
          >
            <p className="mb-3 text-xs font-semibold text-fg-muted uppercase">
              Aperçu en mode {viewportMode}
            </p>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} gap={3}>
              <Card variant="raised">
                <CardBody>
                  <p className="text-xs font-bold text-accent">Carte 1</p>
                  <p className="mt-1 text-xs text-fg-muted">Adaptation automatique</p>
                </CardBody>
              </Card>
              <Card variant="raised">
                <CardBody>
                  <p className="text-xs font-bold text-accent">Carte 2</p>
                  <p className="mt-1 text-xs text-fg-muted">Espacement réactif</p>
                </CardBody>
              </Card>
              <Card variant="raised">
                <CardBody>
                  <p className="text-xs font-bold text-accent">Carte 3</p>
                  <p className="mt-1 text-xs text-fg-muted">Sans media query</p>
                </CardBody>
              </Card>
            </SimpleGrid>
          </div>
        </div>
      </div>

      <SubSection title="Stack, VStack & HStack" hint="Direction, gap et séparateurs automatiques">
        <Preview
          layout="stack"
          label="stack.tsx"
          code={`<VStack gap={3} divider={<Separator />}>
  <HStack justify="between">
    <span>Statut du serveur</span>
    <Badge tone="success">En ligne</Badge>
  </HStack>
  <HStack justify="between">
    <span>Latence réseau</span>
    <span>14 ms</span>
  </HStack>
</VStack>`}
        >
          <VStack gap={4} className="w-full">
            <p className="text-xs font-semibold text-fg-muted uppercase">
              VStack avec séparateur automatique
            </p>
            <VStack gap={3} divider={<Separator />} className="w-full rounded-lg border border-border p-4">
              <HStack justify="between">
                <span className="text-sm font-medium text-fg">Statut du serveur</span>
                <Badge tone="success">En ligne</Badge>
              </HStack>
              <HStack justify="between">
                <span className="text-sm font-medium text-fg">Latence réseau</span>
                <span className="text-sm text-fg-muted">14 ms</span>
              </HStack>
              <HStack justify="between">
                <span className="text-sm font-medium text-fg">Plateforme</span>
                <Badge tone="neutral">Multiplateforme</Badge>
              </HStack>
            </VStack>
          </VStack>
        </Preview>
      </SubSection>

      <SubSection title="SimpleGrid" hint="minChildWidth : une grille fluide sans media query">
        <Preview
          layout="stack"
          label="grid.tsx"
          code={`<SimpleGrid minChildWidth="200px" gap={3}>
  {items.map((item) => <Card key={item.id}>…</Card>)}
</SimpleGrid>

{/* ou en points de rupture explicites */}
<SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} gap={3}>…</SimpleGrid>`}
        >
          <SimpleGrid minChildWidth="200px" gap={3} className="w-full">
            {[1, 2, 3, 4].map((n) => (
              <Card key={n} variant="raised">
                <CardBody className="p-4">
                  <p className="text-sm font-semibold text-fg">Élément {n}</p>
                  <p className="mt-1 text-xs text-fg-muted">Se redimensionne seul</p>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Preview>
      </SubSection>

      <SubSection title="Split" hint="Barre latérale fixe + contenu fluide">
        <Preview
          layout="stack"
          label="split.tsx"
          code={`<Split sidebarWidth="xs" sidebar={<Nav />}>
  <main>…</main>
</Split>`}
        >
          <Split
            sidebarWidth="xs"
            sidebar={
              <Card variant="sunken" className="h-full p-4">
                <p className="text-xs font-bold text-fg-muted uppercase">Barre latérale</p>

                <Stack gap={2} className="mt-3">
                  <Button size="xs" variant="ghost" fullWidth className="justify-start">
                    Vue générale
                  </Button>
                  <Button size="xs" variant="ghost" fullWidth className="justify-start">
                    Paramètres
                  </Button>
                  <Button size="xs" variant="ghost" fullWidth className="justify-start">
                    Sécurité
                  </Button>
                </Stack>
              </Card>
            }
            className="w-full"
          >
            <Card variant="raised" className="p-5">
              <CardHeader>
                <CardTitle>Contenu principal fluide</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-fg-muted">
                  Sur écran large, la barre latérale prend 240 px et le contenu occupe le reste. Sur
                  smartphone, les deux zones s'empilent.
                </p>
              </CardBody>
            </Card>
          </Split>
        </Preview>
      </SubSection>

      <SubSection title="AspectRatio & ScrollArea">
        <Preview
          layout="stack"
          label="media.tsx"
          code={`<AspectRatio ratio={16 / 9}>
  <img src={cover} alt="" />
</AspectRatio>

<ScrollArea maxHeight="max-h-48" scrollbarVariant="thin">…</ScrollArea>`}
        >
          <div className="grid w-full gap-4 lg:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-semibold text-fg-muted uppercase">AspectRatio (16/9)</p>
            <AspectRatio ratio={16 / 9} className="rounded-lg border border-border bg-surface-sunken">
              <Center className="size-full bg-accent-subtle/30 p-6">
                <VStack gap={2} align="center">
                  <Badge tone="brand">Format 16:9</Badge>
                  <p className="text-xs text-fg-muted">Aucun saut de mise en page</p>
                </VStack>
              </Center>
            </AspectRatio>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-fg-muted uppercase">ScrollArea</p>
            <ScrollArea maxHeight="max-h-48" scrollbarVariant="thin" className="rounded-lg border border-border p-3">
              <VStack gap={2}>
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md bg-surface-sunken p-2.5 text-xs">
                    <span className="font-medium text-fg">Ligne {i + 1}</span>
                    <span className="text-fg-muted">Inertie tactile native</span>
                  </div>
                ))}
              </VStack>
            </ScrollArea>
          </div>
          </div>
        </Preview>
      </SubSection>

      <SubSection title="SafeAreaView" hint="Encoches et barres tactiles mobiles">
        <Preview
          layout="stack"
          label="safe-area.tsx"
          code={`<SafeAreaView edges={["top", "bottom"]}>
  <Screen />
</SafeAreaView>`}
        >
          <Container size="full" className="rounded-lg bg-surface-sunken p-2">
            <SafeAreaView edges={["top", "bottom"]} className="rounded-md border border-accent/30 bg-surface-raised p-4">
              <HStack justify="between">
                <span className="text-xs font-bold text-accent">SafeAreaView</span>
                <Badge tone="success">env(safe-area-inset-*)</Badge>
              </HStack>
              <p className="mt-2 text-xs text-fg-muted">
                Compense la Dynamic Island, l'encoche et la barre d'accueil.
              </p>
            </SafeAreaView>
          </Container>
        </Preview>
      </SubSection>
    </Section>
  );
}
